import { FC, useEffect } from 'react'

// Reasons identify the call-site holding the loader visible. Each caller picks
// a unique value so concurrent loaders (auth gate + player load + project
// preparation) compose via ref-count.
export type GlobalPortalLoaderReason =
  | 'auth-gate'
  | 'portal-landing'
  | 'player-load'
  | 'project-prepare'
  | 'lms-player'

interface IGlobalPortalLoaderProps {
  visible: boolean
  reason: GlobalPortalLoaderReason
}

// Toggles the backend-injected boot-loader (`#boot-loader`) instead of
// rendering a separate React loader. This eliminates the perceived
// "double loader" caused by a fixed-position branded loader fading out into a
// flow-positioned, non-branded React loader inside a layout container — the
// element stays the same DOM node, position and branding never change.
//
// Renders nothing; effect-only. Safe on pages without the boot-loader
// (admin hash-router, FE-only dev server) — falls through silently.
export const GlobalPortalLoader: FC<IGlobalPortalLoaderProps> = ({ visible, reason }) => {
  useEffect(() => {
    const api = window.__bootLoader
    if (!api) {
      return undefined
    }

    if (visible) {
      api.show(reason)
    } else {
      api.hide(reason)
    }

    return () => {
      window.__bootLoader?.hide(reason)
    }
  }, [visible, reason])

  return null
}
